/**
 * BrickToolbox - Dynamic brick search bar for AI agent specialization
 * Loads all 44 bricks from bricksOS/brick-index.json (generated from brick.json files).
 *
 * Created: 2026-02-27 by Douglas
 * Last edited: 2026-02-27 by Douglas — replaced hardcoded grid with dynamic search
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, X, ChevronDown, Zap, Box } from 'lucide-react';

// Static brick index — generated from bricksOS/*/brick.json
// Regenerate: python3 or node script reads all brick.json → writes brick-index.json
import ALL_BRICKS from '@bricksOS/brick-index.json';

// Status dot color
const STATUS_COLORS = {
  mature: '#10B981',
  active: '#3B82F6',
  draft: '#F59E0B',
  unknown: '#6B7280',
};

const BrickToolbox = ({ onBrickSelect, selectedBrick, screenContext }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);

  // Focus search input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Filter bricks by search query (name, description, id)
  const filteredBricks = useMemo(() => {
    if (!searchQuery.trim()) return ALL_BRICKS;
    const q = searchQuery.toLowerCase();
    return ALL_BRICKS.filter(
      (b) =>
        b.id.toLowerCase().includes(q) ||
        b.name.toLowerCase().includes(q) ||
        b.description.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const handleBrickClick = (brick) => {
    if (onBrickSelect) {
      onBrickSelect(brick);
    }
    setIsExpanded(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onBrickSelect) onBrickSelect(null);
    setSearchQuery('');
  };

  const selectedBrickData = ALL_BRICKS.find((b) => b.id === selectedBrick);

  return (
    <div className="border-b border-white/10">
      {/* Collapsed view — show selected brick or prompt */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedBrickData ? (
            <>
              <div
                className="w-5 h-5 rounded flex items-center justify-center shrink-0 text-[10px] font-bold"
                style={{
                  backgroundColor: `${STATUS_COLORS[selectedBrickData.status] || '#6B7280'}20`,
                  color: STATUS_COLORS[selectedBrickData.status] || '#6B7280',
                }}
              >
                <Box size={12} />
              </div>
              <span className="text-sm text-white/80 truncate">{selectedBrickData.name}</span>
              <span className="text-[10px] text-white/30 shrink-0">
                {selectedBrickData.capabilities} caps
              </span>
              <button
                onClick={handleClear}
                className="ml-auto p-0.5 hover:bg-white/10 rounded shrink-0"
              >
                <X size={12} className="text-white/40" />
              </button>
            </>
          ) : (
            <>
              <Zap size={14} className="text-[#AEA36D] shrink-0" />
              <span className="text-sm text-white/50">Pick a brick...</span>
            </>
          )}
        </div>
        {!selectedBrickData && (
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown size={14} className="text-white/40" />
          </motion.div>
        )}
      </button>

      {/* Expanded search panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {/* Search input */}
            <div className="px-3 pt-2 pb-1">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search 44 bricks..."
                  className="w-full pl-8 pr-8 py-1.5 bg-white/5 border border-white/10 rounded-md text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:border-[#AEA36D]/50"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsExpanded(false);
                      setSearchQuery('');
                    }
                    // Enter selects first match
                    if (e.key === 'Enter' && filteredBricks.length > 0) {
                      handleBrickClick(filteredBricks[0]);
                    }
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    <X size={12} className="text-white/30 hover:text-white/60" />
                  </button>
                )}
              </div>
            </div>

            {/* Brick list */}
            <div className="max-h-64 overflow-y-auto px-2 pb-2">
              {filteredBricks.length === 0 ? (
                <div className="text-center text-white/30 text-xs py-4">
                  No bricks match &quot;{searchQuery}&quot;
                </div>
              ) : (
                filteredBricks.map((brick) => {
                  const isSelected = selectedBrick === brick.id;
                  const dotColor = STATUS_COLORS[brick.status] || '#6B7280';

                  return (
                    <button
                      key={brick.id}
                      onClick={() => handleBrickClick(brick)}
                      className={`
                        w-full flex items-start gap-2.5 px-2.5 py-2 rounded-md text-left transition-all
                        ${isSelected ? 'bg-white/10 ring-1 ring-[#AEA36D]/30' : 'hover:bg-white/5'}
                      `}
                    >
                      {/* Status dot */}
                      <div
                        className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                        style={{ backgroundColor: dotColor }}
                        title={brick.status}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/80 font-medium truncate">
                            {brick.name}
                          </span>
                          <span className="text-[10px] text-white/25 shrink-0">
                            {brick.capabilities}
                          </span>
                        </div>
                        <p className="text-[11px] text-white/40 truncate leading-tight mt-0.5">
                          {brick.description}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Context awareness hint */}
            {screenContext?.view && (
              <div className="px-3 pb-2">
                <div className="text-[10px] text-purple-400/70 bg-purple-500/10 rounded px-2 py-1">
                  <span className="text-purple-400">ctx:</span> {screenContext.view}
                  {screenContext.selectedClient?.name && ` → ${screenContext.selectedClient.name}`}
                  {screenContext.weekNumber && ` → Week ${screenContext.weekNumber}`}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrickToolbox;
